import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useSalesStatus } from "../components/PhysicalSalesBanner";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import DigitalPushHero from "../components/DigitalPushHero";
import TrustBanner from "../components/TrustBanner";
import PedagogicalWidget from "../components/PedagogicalWidget";
import CategoryBento from "../components/CategoryBento";
import ProductCard from "../components/ProductCard";
import ProductCarousel from "../components/ProductCarousel";
import NeedsBlock from "../components/NeedsBlock";
import Testimonials from "../components/Testimonials";
import Storytelling from "../components/Storytelling";
import DealOfMomentHero from "../components/DealOfMomentHero";
import PressBadges from "../components/PressBadges";
import SemanticSearchBar from "../components/SemanticSearchBar";
import Seo from "../components/Seo";
import ShippingInfoBlock from "../components/ShippingInfoBlock";
import { tr } from "../i18n/translations";
import { toast } from "sonner";

export default function Home() {
  const { addToCart, lang } = useApp();
  const { should_block_physical, showBlockingModal } = useSalesStatus();
  const [categories, setCategories] = useState([]);
  const [promos, setPromos] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [resources, setResources] = useState([]);
  const [newItems, setNewItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pedagogical, setPedagogical] = useState({});

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/products?promo=true&limit=20").then((r) => setPromos(r.data));
    api.get("/products?bestseller=true&limit=20").then((r) => setBestsellers(r.data)).catch(() => {
      api.get("/products?featured=true&limit=20").then((r) => setBestsellers(r.data)).catch(() => setBestsellers([]));
    });
    api.get("/products?featured=true&limit=8").then((r) => setFavorites(r.data));
    // Ressources à utiliser en séance : PDF actifs récents (max 12 pour carrousel)
    api.get("/resources?limit=12").then((r) => {
      const items = (r.data?.items || []).map((x) => ({
        id: x.id,
        slug: x.slug,
        name: { fr: x.titre || "" },
        price: x.prix || 0,
        images: x.cover ? [x.cover] : (x.apercu ? [x.apercu] : []),
        resource_kind: "resource",
        is_digital: true,
      }));
      setResources(items);
    }).catch(() => setResources([]));
    // Nouveautés : produits ajoutés récemment (basé sur created_at côté API)
    api.get("/products?new=true&limit=12").then((r) => setNewItems(r.data)).catch(() => setNewItems([]));
    api.get("/site-reviews").then((r) => setReviews(r.data));
    api.get("/pedagogical").then((r) => setPedagogical(r.data));
  }, []);

  function handleAdd(p) {
    const isPhysical = !p?.is_digital && !p?.no_shipping;
    if (should_block_physical && isPhysical) {
      showBlockingModal();
      return;
    }
    addToCart(p);
    toast.success("Ajouté au panier");
  }

  return (
    <div>
      <Seo
        title="Orthophonie — Matériel, ressources & applications pour orthophonistes"
        description="Matériel d'orthophonie, ressources PDF téléchargeables et applications pour orthophonistes : dyslexie, dyspraxie, articulation, langage oral, phonologie. Outils cliniques sélectionnés. Livraison offerte dès 100€ (France & UE)."
        path="/"
        keywords="orthophonie, orthophoniste, matériel pour orthophonistes, ressources orthophonie, PDF orthophonie, application orthophonie, dyslexie, dyspraxie, dyscalculie, langage oral, articulation, phonologie, troubles dys, Borel-Maisonny, Montessori"
      />
      <Marquee />
      <Hero reviews={reviews} />
      <DigitalPushHero />
      <DealOfMomentHero compact={true} />
      <TrustBanner />
      <ShippingInfoBlock variant="hero" />
      <PressBadges />
      <SemanticSearchBar />
      <NeedsBlock />
      <PedagogicalWidget content={pedagogical} />
      <CategoryBento categories={categories} />

      {/* Carrousel Promotions du moment — swipeable, alimenté par vraies données */}
      <ProductCarousel
        title="Promotions du moment"
        subtitle="Nos ressources et matériel d'orthophonie à prix réduit"
        items={promos}
        showPercent={true}
        testId="carousel-promos"
      />

      {/* PRIORITÉ MAXIMALE — Ressources digitales à utiliser en séance */}
      <ProductCarousel
        title="Ressources à utiliser en séance"
        subtitle="PDF téléchargeables, packs et supports thérapeutiques — utilisables immédiatement"
        items={resources}
        badge="PDF"
        testId="carousel-resources"
        emptyMessage="Nouveaux PDF bientôt disponibles."
      />

      <ProductCarousel
        title="Best-sellers"
        subtitle="Les ressources préférées des orthophonistes"
        items={bestsellers}
        badge="Best-seller"
        testId="carousel-bestsellers"
        emptyMessage="Classement en cours de constitution."
      />

      {/* Nouveautés — basé sur created_at (aucune donnée inventée) */}
      <ProductCarousel
        title="Nouveautés"
        subtitle="Les derniers outils ajoutés au catalogue"
        items={newItems}
        testId="carousel-new"
        emptyMessage=""
      />

      {favorites.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="favorites-section">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-ink/60 mb-3">Sélection cliniciens</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
              {tr(lang, "fav_title")}
            </h2>
            <p className="mt-3 text-ink-soft">{tr(lang, "fav_sub")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {favorites.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} onAdd={handleAdd} />)}
          </div>
        </section>
      )}

      <Testimonials reviews={reviews} />
      <Storytelling />
    </div>
  );
}
